import {
  HttpErrorResponse,
  HttpEvent,
  HttpHandler,
  HttpHeaders,
  HttpInterceptor as AngularHttpInterceptor,
  HttpRequest,
  HttpResponse,
} from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { BehaviorSubject, Observable, of, Subject } from 'rxjs';
import { filter, map, take } from 'rxjs/operators';
import { UserService } from './user.services';
import { UserServiceDataService } from '../data-services/identity/user-service.data.service';
import { NotificationService } from './notification.service';
import { UserMenuService } from './user-menu.service';
import { AdminProfileCompletionService } from './admin-profile-completion.service';

/** Queued waiters receive this when refresh fails so they do not hang. */
const refreshFailedSentinel = '__dashboard_auth_refresh_failed__';

@Injectable()
export class AuthHttpInterceptor implements AngularHttpInterceptor {
  refreshTokenInProgress = false;
  private refreshTokenSubject = new BehaviorSubject<string | null>(null);

  constructor(
    private userService: UserService,
    private userDateService: UserServiceDataService,
    private notificationService: NotificationService,
    private userMenuService: UserMenuService,
    private adminProfileService: AdminProfileCompletionService,
    private router: Router
  ) {}

  intercept(
    request: HttpRequest<any>,
    next: HttpHandler
  ): Observable<HttpEvent<any>> {
    const interceptObservable = new Subject<HttpEvent<any>>();
    const modifiedRequest = this.normalizeRequestHeaders(request);

    if (this.isPublicRequest(modifiedRequest.url)) {
      next.handle(modifiedRequest).subscribe({
        next: (event: HttpEvent<any>) => {
          this.handleSuccessResponse(event, interceptObservable);
        },
        error: (error: any) => {
          interceptObservable.error(error);
          interceptObservable.complete();
        },
      });
      return interceptObservable;
    }

    if (
      !modifiedRequest.url.includes('RefreshToken') &&
      !modifiedRequest.url.includes('Login')
    ) {
      this.refreshToken(modifiedRequest, next).subscribe((res) => {
        next
          .handle(res)
          .subscribe({
            next: (event: HttpEvent<any>) => {
              this.handleSuccessResponse(event, interceptObservable);
            },
            error: (error: any) => {
              this.handleErrorResponse(
                error,
                interceptObservable,
                next,
                modifiedRequest
              ).subscribe();
            },
          });
      });
    } else {
      next
        .handle(modifiedRequest)
        .subscribe({
          next: (event: HttpEvent<any>) => {
            this.handleSuccessResponse(event, interceptObservable);
          },
          error: (error: any) => {
            this.handleErrorResponse(
              error,
              interceptObservable,
              next,
              modifiedRequest
            ).subscribe();
          },
        });
    }
    return interceptObservable;
  }

  private isPublicRequest(url: string): boolean {
    const u = (url || '').toLowerCase();
    return (
      u.includes('/assets/') ||
      u.includes('/i18n/') ||
      u.includes('refreshtoken') ||
      u.includes('/dashauthentication/login') ||
      u.includes('/dashauthentication/validatecredentials') ||
      u.includes('/dashauthentication/forgetpassword') ||
      u.includes('/dashauthentication/resetpassword')
    );
  }

  private isAuthRoute(): boolean {
    const hash = typeof window !== 'undefined' ? window.location.hash : '';
    const url = `${hash}${this.router.url || ''}`.toLowerCase();
    return url.includes('/authentication') || url.includes('/auth');
  }

  protected normalizeRequestHeaders(
    request: HttpRequest<any>
  ): HttpRequest<any> {
    let modifiedHeaders = new HttpHeaders();
    if (!this.isPublicRequest(request.url)) {
      modifiedHeaders = this.addAuthorizationHeaders(request.headers);
    }

    return request.clone({
      headers: modifiedHeaders,
    });
  }

  protected addAuthorizationHeaders(headers: HttpHeaders): HttpHeaders {
    const token = this.userService.getToken();
    if (headers && token) {
      headers = headers.set('Authorization', 'Bearer ' + token);
    }
    const raw = localStorage.getItem('currentLang') || 'en';
    const acceptLang = raw.toLowerCase().startsWith('ar')
      ? 'ar'
      : raw.toLowerCase().startsWith('en')
        ? 'en'
        : raw;
    headers = headers.set('Accept-Language', acceptLang);
    return headers;
  }

  private handleSuccessResponse(
    event: HttpEvent<any>,
    interceptObservable: Subject<HttpEvent<any>>
  ): void {
    if (event instanceof HttpResponse) {
      if (
        event.body instanceof Blob &&
        event.body.type &&
        event.body.type.indexOf('application/json') >= 0
      ) {
        this.blobToText(event.body).subscribe((json) => {
          const responseBody = json === 'null' ? {} : JSON.parse(json);

          const modifiedResponse = this.handleResponse(
            event.clone({ body: responseBody })
          );

          interceptObservable.next(
            modifiedResponse.clone({
              body: new Blob([JSON.stringify(modifiedResponse.body)], {
                type: 'application/json',
              }),
            })
          );

          interceptObservable.complete();
        });
      } else {
        interceptObservable.next(event);
        interceptObservable.complete();
      }
    } else {
      interceptObservable.next(event);
    }
  }

  blobToText(blob: any): Observable<string> {
    return new Observable<string>((observer: any) => {
      if (!blob) {
        observer.next('');
        observer.complete();
      } else {
        const reader = new FileReader();
        reader.onload = (event) => {
          observer.next((event.target as any).result);
          observer.complete();
        };
        reader.readAsText(blob);
      }
    });
  }

  private handleResponse(response: HttpResponse<any>): HttpResponse<any> {
    const ajaxResponse = this.getAjaxResponseOrNull(response);
    if (ajaxResponse == null) {
      return response;
    }
    return this.handleSREFResponse(response, ajaxResponse);
  }

  private getAjaxResponseOrNull(
    response: HttpResponse<any>
  ): IStatusResultResponse | null {
    if (!response || !response.headers) {
      return null;
    }

    const contentType = response.headers.get('Content-Type');
    if (!contentType) {
      return null;
    }

    if (contentType.indexOf('application/json') < 0) {
      return null;
    }

    const responseObj = JSON.parse(JSON.stringify(response.body));
    return responseObj as IStatusResultResponse;
  }

  private handleSREFResponse(
    response: HttpResponse<any>,
    ajaxResponse: IStatusResultResponse
  ): HttpResponse<any> {
    if (ajaxResponse.status !== 'Success' && ajaxResponse.message) {
      this.notificationService.error(ajaxResponse.message);
    }

    return response.clone({
      body: ajaxResponse.response,
    });
  }

  private handleErrorResponse(
    error: any,
    interceptObservable: Subject<HttpEvent<any>>,
    next: HttpHandler,
    req: HttpRequest<any>
  ): Observable<any> {
    const errorObservable = new Subject<any>();

    if (!(error.error instanceof Blob)) {
      interceptObservable.error(error);
      interceptObservable.complete();
      if (error instanceof HttpErrorResponse && error.status === 401) {
        this.logoutIfAuthenticatedSession();
      }
      if (
        error instanceof HttpErrorResponse &&
        error.status === 403 &&
        this.shouldAutoLogoutForForbidden(req)
      ) {
        this.logoutIfAuthenticatedSession();
      }
      return of({});
    }

    if (error instanceof HttpErrorResponse && error.status === 401) {
      this.logoutIfAuthenticatedSession();
    }

    this.blobToText(error.error).subscribe((json) => {
      const errorBody = json === '' || json === 'null' ? {} : JSON.parse(json);
      const code = errorBody?.code ?? errorBody?.response?.code;
      if (
        error instanceof HttpErrorResponse &&
        error.status === 403 &&
        code !== 'RequireAdminProfileCompletion' &&
        this.shouldAutoLogoutForForbidden(req)
      ) {
        this.logoutIfAuthenticatedSession();
      }
      if (error instanceof HttpErrorResponse && error.status === 403 && code === 'RequireAdminProfileCompletion') {
        this.router.navigate(['/dashboard']).then(() => window.location.reload());
      }

      const errorResponse = new HttpResponse({
        headers: error.headers,
        status: error.status,
        body: errorBody,
      });

      const ajaxResponse = this.getAjaxResponseOrNull(errorResponse);

      if (ajaxResponse != null) {
        this.handleSREFResponse(errorResponse, ajaxResponse);
      } else {
        this.handleSREFErrorResponse(errorResponse);
      }

      errorObservable.complete();
      interceptObservable.error(error);
      interceptObservable.complete();
    });
    return errorObservable;
  }

  private shouldAutoLogoutForForbidden(req: HttpRequest<any>): boolean {
    const url = (req?.url || '').toLowerCase();
    return !(
      url.includes('/dashauthentication/login') ||
      url.includes('/dashauthentication/forgetpassword') ||
      url.includes('/dashauthentication/resetpassword')
    );
  }

  private handleSREFErrorResponse(response: any): void {
    if (response?.status === 400 && response.body?.status === 'Failure') {
      const errors = response.body?.response?.errors;
      if (errors) {
        for (const key in errors) {
          if (errors.hasOwnProperty(key)) {
            const fieldName = key.includes('.') ? key.substring(key.indexOf('.') + 1) : key;
            this.notificationService.error(`${fieldName}: ${errors[key]}`);
          }
        }
      }
    }
  }

  private logoutIfAuthenticatedSession(): void {
    if (this.isAuthRoute()) {
      this.userService.clearStaleSession();
      return;
    }
    if (this.userService.isLoggedIn()) {
      this.userService.logout();
    }
  }

  private refreshToken(
    req: HttpRequest<any>,
    _next: HttpHandler
  ): Observable<HttpRequest<any>> {
    const sessionExpired =
      !!this.userService.getSessionExpiryDate() &&
      new Date(parseInt(this.userService.getSessionExpiryDate()!, 10)) <= new Date();

    if (
      this.isAuthRoute() ||
      !this.userService.isLoggedIn() ||
      !sessionExpired
    ) {
      return of(req);
    }

    if (this.refreshTokenInProgress) {
      return this.refreshTokenSubject.pipe(
        filter((token): token is string => token != null),
        take(1),
        filter((token) => token !== refreshFailedSentinel),
        map(() => this.normalizeRequestHeaders(req))
      );
    }

    const refreshTokenValue = this.userService.getRefreshToken();
    if (!refreshTokenValue) {
      this.logoutIfAuthenticatedSession();
      return new Observable((sup) => {
        sup.complete();
      });
    }

    this.refreshTokenInProgress = true;
    this.refreshTokenSubject.next(null);

    return new Observable((o) => {
      this.userDateService
        .refreshToken({ refreshToken: refreshTokenValue })
        .subscribe({
          next: (res) => {
            const resource =
              (res as any)?.resource ?? (res as any)?.response ?? res;
            const token = resource?.token ?? resource?.Token;
            const newRefresh =
              resource?.refreshToken ?? resource?.RefreshToken;
            const expiry =
              resource?.expiryTokenInMinutes ??
              resource?.ExpiryTokenInMinutes;

            if (!token) {
              this.refreshTokenInProgress = false;
              this.refreshTokenSubject.next(refreshFailedSentinel);
              this.logoutIfAuthenticatedSession();
              o.complete();
              return;
            }

            this.userService.setToken(token);
            if (newRefresh) {
              this.userService.setRefreshToken(newRefresh);
            }
            if (expiry) {
              this.userService.setSessionExpiryDate(expiry);
            }

            this.refreshTokenInProgress = false;
            this.refreshTokenSubject.next(token);
            o.next(this.normalizeRequestHeaders(req));
            o.complete();
          },
          error: () => {
            this.refreshTokenInProgress = false;
            this.refreshTokenSubject.next(refreshFailedSentinel);
            this.logoutIfAuthenticatedSession();
            o.complete();
          },
        });
    });
  }
}

export interface IStatusResultResponse {
  status: string;
  exceptionMessage?: string;
  message?: string;
  response?: any;
}
